import { gsap } from 'gsap';

import { getAnimationSystem, initializeAnimationSystem } from './components-frontend/UnifiedAnimationSystem.js';
import { BasicAnimationsModule } from './components-frontend/modules/BasicAnimationsModule.js';
import { MouseDistanceModule } from './components-frontend/modules/MouseDistanceModule.js';
import { TextAnimationsModule } from './components-frontend/modules/TextAnimationsModule.js';
import { ParallaxModule } from './components-frontend/modules/ParallaxModule.js';

import { 
    initIntersectionTrigger,
    initScrollTrigger,
    initHoverTrigger,
    initClickTrigger,
    initAdvancedScrollTrigger
} from './components-frontend/AdvancedTriggers.js';

import { ScrollProgressCalculator } from './components-frontend/ScrollProgressCalculator.js';
import { touchState } from './components-frontend/methods.js';

import './style.scss';

if (typeof window !== 'undefined') {
    window.ScrollProgressCalculator = ScrollProgressCalculator;
}

let animationSystem = null;

/**
 * ============================================================================
 * ✅ OPTIMIZED: Zero copying dla nowej struktury
 * ============================================================================
 */
const normalizeAnimationData = function(rawData) {
    if (!rawData) return getDefaultAnimationData();
    return rawData;
};

function getDefaultAnimationData() {
    return {
        enabled: true,
        eventsConfig: {
            triggerType: 'intersection',
            triggerSettings: {
                intersection: { threshold: 0, offset: 0, once: true },
                scroll: { useAnimationArea: false, animationStart: 0, animationEnd: 100 },
                hover: { triggerOnMouseEnter: true, triggerOnMouseLeave: false, reverseOnLeave: true },
                click: { toggleOnClick: false, reverseOnSecondClick: false },
                mousePosition: { 
                    maxDistance: null, minDistance: 0, sensitivity: 1, smoothing: 0.15,
                    falloffType: 'quadratic', distanceMode: 'center',
                    mobileFallback: { enabled: true, fallbackType: 'intersection', fallbackSettings: {} }
                }
            }
        },
        basicAnimations: {
            enabled: true,
            timing: { duration: 1, delay: 0, ease: 'power2.out', loop: false, loopDelay: 0, yoyo: false },
            initialState: { opacity: 1 },
            finalState: { opacity: 1 }
        },
        advancedAnimations: {
            textAnimations: { enabled: false }
        },
        useParallax: false,
        useMouseParallax: false
    };
}

function initializeUnifiedSystem() {
    if (animationSystem) return animationSystem;
    
    animationSystem = getAnimationSystem();
    
    animationSystem.registerModule('basicAnimations', new BasicAnimationsModule());
    animationSystem.registerModule('mouseDistance', new MouseDistanceModule());
    animationSystem.registerModule('textAnimations', new TextAnimationsModule());
    animationSystem.registerModule('parallax', new ParallaxModule());
    
    window.animationSystem = animationSystem;
    
    return animationSystem;
}

function canUseUnifiedSystem(triggerType, animationData) {
    const supportedTriggers = ['intersection', 'scroll', 'hover', 'click', 'mousePosition'];
    return supportedTriggers.includes(triggerType);
}

function setupAnimationAreaScrub(block, animationData, elementId) {
    const scrollSettings = animationData.eventsConfig.triggerSettings.scroll;
    
    if (!scrollSettings || !scrollSettings.useAnimationArea) {
        return null;
    }
    
    const progressCalculator = new ScrollProgressCalculator(block, {
        animationStart: scrollSettings.animationStart || 0,
        animationEnd: scrollSettings.animationEnd || 100,
        scrollDirection: scrollSettings.scrollDirection || 'vertical'
    });
    
    const elementData = animationSystem.elements.get(block);
    if (!elementData) return null;
    
    const basicEffect = elementData.activeEffects.get('basicAnimations');
    if (!basicEffect?.module) return null;
    
    gsap.set(block, basicEffect.state.initialProps);
    
    progressCalculator.onProgress((progress) => {
        if (basicEffect.module.setProgress) {
            basicEffect.module.setProgress(basicEffect.state, progress);
        }
    });
    
    progressCalculator.onEnter(() => {
        block.classList.add('in-animation-area');
    });
    
    progressCalculator.onLeave(() => {
        block.classList.remove('in-animation-area');
    });
    
    return progressCalculator;
}

function animateElementToFinal(elementData) {
    const basicEffect = elementData.activeEffects.get('basicAnimations');
    if (basicEffect?.module?.animateToFinal) {
        basicEffect.module.animateToFinal(elementData.element, basicEffect.state);
    }
    
    const textEffect = elementData.activeEffects.get('textAnimations');
    if (textEffect?.module?.executeAnimation && textEffect.state?.enabled) {
        textEffect.module.executeAnimation(textEffect.state);
    }
}

function animateElementToInitial(elementData) {
    const basicEffect = elementData.activeEffects.get('basicAnimations');
    if (basicEffect?.module?.animateToInitial) {
        basicEffect.module.animateToInitial(elementData.element, basicEffect.state);
    }
    
    const textEffect = elementData.activeEffects.get('textAnimations');
    if (textEffect?.state?.enabled) {
        if (textEffect.module?.setProgress) {
            textEffect.module.setProgress(textEffect.state, 0);
        } else if (textEffect.module?.reset) {
            textEffect.module.reset(textEffect.state);
        }
    }
}

const initializeBlockAnimations = function(block) {
    if (!block || block._offAnimationInitialized) {
        return null;
    }
    
    let animationData;
    try {
        const rawData = block.dataset.gsapAnimation || '{}';
        animationData = normalizeAnimationData(JSON.parse(rawData)); // ✅ OPTIMIZED
    } catch (error) {
        console.error(`❌ Error parsing animation data for ${block.id}:`, error);
        return null;
    }
    
    if (!animationData || animationData.enabled === false) {
        return null;
    }
    
    block._offAnimationInitialized = true;
    
    if (!animationSystem) {
        initializeUnifiedSystem();
    }
    
    const triggerType = animationData.eventsConfig.triggerType;
    const canUseUnified = canUseUnifiedSystem(triggerType, animationData);
    
    if (canUseUnified) {
        const elementId = animationSystem.registerElement(block, animationData);
        
        if (triggerType === 'scroll' && animationData.eventsConfig.triggerSettings.scroll.useAnimationArea) {
            const progressCalculator = setupAnimationAreaScrub(block, animationData, elementId);
            if (progressCalculator) {
                block._progressCalculator = progressCalculator;
            }
        } else {
            setupDiscreteTrigger(block, triggerType, animationData, elementId);
        }
        
        return elementId;
    }
    
    return null;
};

function setupDiscreteTrigger(block, triggerType, animationData, elementId) {
    const triggerConfig = animationData.eventsConfig;
    
    const animationConfig = {
        duration: animationData.basicAnimations.timing.duration,
        delay: animationData.basicAnimations.timing.delay,
        ease: animationData.basicAnimations.timing.ease,
        once: triggerType === 'intersection' 
            ? (triggerConfig.triggerSettings[triggerType]?.once !== false)
            : false,
        
        customAnimationFunction: () => {
            const elementData = animationSystem.elements.get(block);
            if (elementData) {
                animateElementToFinal(elementData);
            }
            return gsap.timeline();
        },
        
        customReverseAnimationFunction: () => {
            const elementData = animationSystem.elements.get(block);
            if (elementData) {
                animateElementToInitial(elementData);
            }
            return gsap.timeline();
        }
    };
    
    let triggerEffect = null;
    
    switch (triggerType) {
        case 'intersection':
            triggerEffect = initIntersectionTrigger(block, triggerConfig.triggerSettings.intersection, animationConfig);
            break;
        case 'scroll':
            triggerEffect = initScrollTrigger(block, triggerConfig.triggerSettings.scroll, animationConfig);
            break;
        case 'hover':
            triggerEffect = initHoverTrigger(block, triggerConfig.triggerSettings.hover, animationConfig);
            break;
        case 'click':
            triggerEffect = initClickTrigger(block, triggerConfig.triggerSettings.click, animationConfig);
            break;
        case 'mousePosition':
            break;
    }
    
    if (triggerEffect) {
        block._discreteTriggerEffect = triggerEffect;
    }
}

/**
 * ✅ ZACHOWANE: Cała funkcja initAnimations z trackowaniem
 */
const initAnimations = function() {
    initializeUnifiedSystem();
    
    const animationBlocks = document.querySelectorAll('.off-animation-block');
    
    let successCount = 0;
    let errorCount = 0;
    let mobileCount = 0;
    let scrollAreaCount = 0;
    
    animationBlocks.forEach(block => {
        try {
            const elementId = initializeBlockAnimations(block);
            
            if (elementId) {
                successCount++;
                
                if (touchState.isMobile) {
                    mobileCount++;
                }
                
                if (block._progressCalculator) {
                    scrollAreaCount++;
                }
            }
        } catch (error) {
            console.error(`❌ Error initializing block ${block.id}:`, error);
            errorCount++;
        }
    });
};

/**
 * ✅ ZACHOWANE: MutationObserver dla dynamicznych bloków
 */
if (typeof MutationObserver !== 'undefined') {
    const globalObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType === 1) {
                        let blocksToInit = [];
                        
                        if (node.classList && node.classList.contains('off-animation-block')) {
                            blocksToInit.push(node);
                        }
                        
                        if (node.querySelectorAll) {
                            const nestedBlocks = node.querySelectorAll('.off-animation-block');
                            blocksToInit.push(...nestedBlocks);
                        }
                        
                        blocksToInit.forEach(function(block) {
                            try {
                                initializeBlockAnimations(block);
                            } catch (error) {
                                console.error(`❌ Error initializing dynamic block ${block.id}:`, error);
                            }
                        });
                    }
                });
            }
        });
    });
    
    globalObserver.observe(document.body, {
        childList: true,
        subtree: true
    });
}

// /**
//  * DOMContentLoaded
//  */
// document.addEventListener('DOMContentLoaded', () => {
//     initAnimations();
// });
// init once for barba. Observer will do the rest
if ( !window.arcanovMotionFrontendInitialized ) {
    window.arcanovMotionFrontendInitialized = true;
    initAnimations();
}

/**
 * ✅ ZACHOWANE: Global API
 */
window.offScrollAnimations = {
    system: () => animationSystem,
    
    reinitialize: function() {
        if (animationSystem) {
            animationSystem.destroy();
            animationSystem = null;
        }
        
        document.querySelectorAll('.off-animation-block').forEach(block => {
            block._offAnimationInitialized = false;
            if (block._progressCalculator) {
                block._progressCalculator.destroy();
                delete block._progressCalculator;
            }
        });
        
        initAnimations();
    },
    
    getStats: function() {
        if (animationSystem) {
            const baseStats = animationSystem.getStats();
            
            let mobileStats = { mobileElements: 0, fallbackTypes: {}, deviceInfo: null };
            let scrollAreaStats = { scrollAreaElements: 0, discreteElements: 0 };
            
            animationSystem.elements.forEach((elementData) => {
                const mouseEffect = elementData.activeEffects.get('mouseDistance');
                if (mouseEffect?.state?.isMobileFallback) {
                    mobileStats.mobileElements++;
                    const fallbackType = mouseEffect.state.fallbackType;
                    mobileStats.fallbackTypes[fallbackType] = (mobileStats.fallbackTypes[fallbackType] || 0) + 1;
                    
                    if (!mobileStats.deviceInfo && mouseEffect.state.deviceInfo) {
                        mobileStats.deviceInfo = mouseEffect.state.deviceInfo;
                    }
                }
                
                if (elementData.element._progressCalculator) {
                    const scrollSettings = elementData.config?.eventsConfig?.triggerSettings?.scroll;
                    if (scrollSettings?.useAnimationArea) {
                        scrollAreaStats.scrollAreaElements++;
                    } else {
                        scrollAreaStats.discreteElements++;
                    }
                }
            });
            
            return { ...baseStats, mobile: mobileStats, scrollArea: scrollAreaStats };
        }
        return { error: 'System not initialized' };
    }
};